import React, { createContext, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

/**
 * @typedef {Object} GeneralContextValue
 */

/** @type {React.Context<GeneralContextValue | undefined>} */
const GeneralContext = createContext(undefined);

/**
 * GeneralProvider - Context provider for General state.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */

export const GeneralProvider = ({ children }) => {
  const { id: projectId } = useParams();
  const [pageViews, setPageViews] = useState({});
  const [pages, setPages] = useState([]);
  const [generalProjectPath, setGeneralProjectPath] = useState(null);
  const [selectedModelingRevision, setSelectedModelingRevision] = useState("0");
  const [modelingData, setModelingData] = useState([]);
  const [type, setType] = useState(null);
  const [tab, setTab] = useState(null);
  const [allTables, setAllTables] = useState({});
  const [revision, setRevision] = useState({});
  const [TablenameDb, getTableData] = useState({});
  const [fastenersTable, setFastenersTable] = useState({
    bolts: [],
    nuts: [],
    washers: [],
  });

  async function getAllTabelsFromDB(tab) {
    // console.log("Fetching all tables for tab:", tab);
    const allTables = await window.electron.db.getAllTables(tab);
    setAllTables(allTables);
    return allTables;
  }

  // async function getRevisionsFromPath(path) {
  //   // Retrieve the available revisions (e.g., folder names or files in the project path)
  //   const availableRevisions =
  //     await window.electron.freecad.getRevisionsFromPath(path);

  //   console.log("Available Revisions:", availableRevisions);
  //   // Extract revision numbers (assuming they are folder names or files with revision numbers)
  //   const revisionNumbers = availableRevisions
  //     .map((rev) => {
  //       // You may need to adjust how you extract revision numbers based on the actual structure
  //       const revisionMatch = rev.match(/\d+/); // This assumes revisions are numeric
  //       return revisionMatch ? revisionMatch[0] : null;
  //     })
  //     .filter(Boolean); // Remove any null or invalid revisions

  //   // Add 0 to revisions array if it's not present
  //   if (!revisionNumbers.includes("0")) {
  //     revisionNumbers.unshift("0");
  //   }

  //   // return revisionNumbers;
  //   // Update modelingData with new revisions, ensuring each revision has a corresponding key in the modelingData
  //   setModelingData((prev) => {
  //     // const newModelingData = { ...prev };
  //     const newModelingData = {};

  //     // Loop through the revisions and add each one to modelingData if it doesn't exist
  //     revisionNumbers.forEach((rev) => {
  //       if (!newModelingData[rev]) {
  //         // Add a default value (empty object) for the revision key if it doesn't exist
  //         newModelingData[rev] = {};
  //       }
  //     });

  //     return {
  //       ...newModelingData,
  //       // revisions: revisionNumbers, // Store the array of revisions in the modelingData state
  //     };
  //   });

  //   // Optionally, set the default selected revision
  //   if (revisionNumbers.length > 0) {
  //     setSelectedModelingRevision(revisionNumbers[0]); // Select the first revision by default
  //     setRevision(revisionNumbers[0]);
  //   }
  // }

  async function getRevisionsFromPath(path) {
    const availableRevisions =
      await window.electron.freecad.getRevisionsFromPath(path);

    const revisionNumbers = availableRevisions
      .map((rev) => {
        const revisionMatch = rev.match(/\d+/);
        return revisionMatch ? revisionMatch[0] : null;
      })
      .filter(Boolean);

    if (!revisionNumbers.includes("0")) {
      revisionNumbers.unshift("0");
    }

    setModelingData((prev) => {
      const newModelingData = { ...prev }; // ✅ preserve existing data

      revisionNumbers.forEach((rev) => {
        if (!newModelingData[rev]) {
          newModelingData[rev] = {}; // ✅ only add missing keys, don't overwrite
        }
      });

      return newModelingData;
    });

    if (revisionNumbers.length > 0) {
      setSelectedModelingRevision(revisionNumbers[0]);
      setRevision(revisionNumbers[0]);
    }
  }

  async function loadPath(id) {
    try {
      const res = await window.electron.db.getProjectPath(id);
      if (res) {
        console.log("Loaded project path from DB:", res);
        setGeneralProjectPath(res);
        await getRevisionsFromPath(res);
        return res;
      } else {
        return null;
      }
    } catch (err) {
      console.error("Error loading project path:", err);
      return null;
    }
  }

  useEffect(() => {
    async function fetchTables() {
      if (tab) {
        const tables = await getAllTabelsFromDB(tab);
        setAllTables(tables);
        // console.log("All tables fetched for tab", tab, ":", tables);
      }
    }

    fetchTables();
  }, [tab]);

  return (
    <GeneralContext.Provider
      value={{
        projectId,
        pageViews,
        setPageViews,
        pages,
        setPages,
        modelingData,
        setModelingData,
        selectedModelingRevision,
        setSelectedModelingRevision,
        generalProjectPath,
        setGeneralProjectPath,
        type,
        setType,
        tab,
        setTab,
        allTables,
        setAllTables,
        getAllTabelsFromDB,
        revision,
        setRevision,
        getRevisionsFromPath,
        loadPath,
        fastenersTable,
        setFastenersTable,
        getTableData,
        TablenameDb,
      }}
    >
      {children}
    </GeneralContext.Provider>
  );
};

/**
 * useGeneralContext - Hook to access piping context.
 *
 * @returns {GeneralContextValue}
 */
export const useGeneralContext = () => {
  const context = useContext(GeneralContext);
  if (!context) {
    throw new Error("useGeneralContext must be used within a GeneralProvider");
  }
  return context;
};
